"use client";

import * as React from "react";
import { nanoid } from 'nanoid';
import { cn } from "@/lib/utils";
import { useLivingCardInteraction } from "@/hooks/useLivingCardInteraction";
import { useLivingCards } from "@/context/livingCards";
import { SpotlightItem } from "@/data/spotlight";

interface SpotlightLivingCardProps {
  children: React.ReactNode;
  spotlight: SpotlightItem;
  className?: string;
  id?: string;
}

/**
 * SpotlightLivingCard - A wrapper component that integrates Spotlight cards with the Living Cards system
 * Handles hover/focus states and animations for spotlight cards
 */
export default function SpotlightLivingCard({
  children,
  spotlight,
  id: propId,
  className = "",
}: SpotlightLivingCardProps) {
  // Generate ID on client side only to avoid hydration mismatch
  const [id, setId] = React.useState<string>("");
  
  // Set the ID once on client-side only
  React.useEffect(() => {
    if (!id) {
      // Use provided ID, spotlight's ID, or generate one
      setId(propId || spotlight.id || nanoid());
    }
  }, [propId, spotlight.id, id]);
  
  const cardRef = React.useRef<HTMLDivElement>(null);
  
  // Get animation context
  const { registerCard, config } = useLivingCards();
  
  // Use the interaction hook
  const { isInteracting } = useLivingCardInteraction(cardRef, id || "temp-id");
  
  // Get tags and size for data attributes
  const tags = spotlight.tags || [];
  const cardSize = spotlight.size || "1x1";
  
  // Register for animations if enabled
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    
    if (config.enabled && cardRef.current && id) {
      // Add CSS class for client-side styling
      const currentCard = cardRef.current;
      currentCard.classList.add('living-card');
      
      const unregister = registerCard(currentCard);
      return () => {
        unregister();
        if (currentCard) {
          currentCard.classList.remove('living-card');
        }
      };
    }
  }, [config.enabled, registerCard, id]);
  
  return (
    <div 
      ref={cardRef}
      className={cn(
        "bg-card text-card-foreground rounded-xl border shadow-sm h-full w-full px-[1.2rem] py-[1.2rem] living-card-container",
        {
          // This class is managed by the interaction hook
          "border-accent": isInteracting
        },
        className
      )}
      data-living-card-id={id}
      data-spotlight-id={id}
      data-spotlight-tags={tags.join(" ")}
      data-spotlight-size={cardSize}
      tabIndex={0} // Make focusable for keyboard users
    >
      {children}
    </div>
  );
}